import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquarePlus, Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import api from "@/lib/api";

const CATEGORIES = [
  { id: "idea", emoji: "💡", label: "I have an idea" },
  { id: "bug", emoji: "🐛", label: "Something isn't working" },
  { id: "confusing", emoji: "😕", label: "Something is confusing" },
  { id: "slow", emoji: "⚡", label: "Something is too slow" },
  { id: "love", emoji: "❤️", label: "I love something" },
  { id: "other", emoji: "💬", label: "Other" },
];

export const FeedbackWidget = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const reset = () => { setCategory(null); setText(""); setDone(false); setErr(""); };

  const submit = async () => {
    if (!text.trim()) { setErr("Please tell us a bit more."); return; }
    setBusy(true);
    setErr("");
    try {
      await api.post("/feedback", {
        category: category.id,
        text: text.trim(),
        page: location.pathname,
      });
      setDone(true);
    } catch {
      setErr("Couldn't send feedback. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="feedback-widget-btn"
        onClick={() => { reset(); setOpen(true); }}
        className="fixed bottom-4 right-4 z-40 inline-flex items-center gap-1.5 rounded-full border border-zinc-700/80 bg-zinc-900/90 backdrop-blur p-2.5 sm:px-3 sm:py-2 text-[11px] font-medium text-zinc-400 hover:text-zinc-100 hover:border-zinc-500 shadow-lg transition-colors"
        title="Send feedback"
        aria-label="Send feedback"
      >
        <MessageSquarePlus className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
        <span className="hidden sm:inline">Feedback</span>
      </button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent data-testid="feedback-dialog" className="border-zinc-800 bg-zinc-950 text-zinc-50 sm:max-w-md">
          {done ? (
            <div className="py-6 text-center space-y-3" data-testid="feedback-thanks">
              <CheckCircle2 className="h-8 w-8 text-emerald-400 mx-auto" />
              <div className="text-sm text-zinc-100 font-medium">
                Thank you. Your feedback helps us improve Intuiterview.
              </div>
              <button
                type="button"
                data-testid="feedback-close-btn"
                onClick={() => setOpen(false)}
                className="text-xs text-zinc-500 hover:text-zinc-200 underline underline-offset-4"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-zinc-50 text-base">Help us make Intuiterview better</DialogTitle>
                {!category && (
                  <DialogDescription className="text-zinc-400 text-xs">
                    What's on your mind?
                  </DialogDescription>
                )}
              </DialogHeader>
              {!category ? (
                <div className="grid grid-cols-1 gap-1.5">
                  {CATEGORIES.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      data-testid={`feedback-cat-${c.id}`}
                      onClick={() => setCategory(c)}
                      className="flex items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900/50 hover:border-zinc-600 hover:bg-zinc-900 px-3.5 py-2.5 text-sm text-zinc-200 text-left transition-colors"
                    >
                      <span className="text-base leading-none">{c.emoji}</span>
                      {c.label}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  <button
                    type="button"
                    data-testid="feedback-back-btn"
                    onClick={() => setCategory(null)}
                    className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-200"
                  >
                    <ArrowLeft className="h-3 w-3" /> {category.emoji} {category.label}
                  </button>
                  <textarea
                    data-testid="feedback-text"
                    autoFocus
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    maxLength={2000}
                    rows={4}
                    placeholder="Tell us more..."
                    className="w-full text-sm rounded-lg border border-zinc-800 bg-zinc-900/60 text-zinc-200 placeholder:text-zinc-600 px-3 py-2.5 focus:outline-none focus:border-zinc-600 resize-none"
                  />
                  {err && <div className="text-xs text-rose-300">{err}</div>}
                  <button
                    type="button"
                    data-testid="feedback-submit-btn"
                    onClick={submit}
                    disabled={busy}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold px-4 py-2.5 text-sm disabled:opacity-60"
                  >
                    {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    Send feedback
                  </button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeedbackWidget;
